import { ColorNeutralLightGrey, ColorNordicGrey5, Spacing32, Spacing4, Spacing8 } from 'autogen/design-tokens/tokens';
import type { FooterUspViewModel } from 'autogen/swagger/all';
import { ArrowLeft, ArrowRight } from 'autogen/translation-keys/trans-website-imagealttext-icons';
import ScrollSnapSlider, {
    type IOptions,
    NavigationButton,
    ScrollSnapSliderContext,
    SlideIndex,
    useScrollSnapSlider
} from 'components/generic/ScrollSnapSlider';
import Icon from 'components/Icon';
import { invisibleScrollbar } from 'modules/helpers/mixins';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import USPItem from './USPItem';

const Wrapper = styled.div`
    display: block;
    background-color: ${ColorNordicGrey5};
    padding: ${Spacing32} ${Spacing8};

    ${breakpoint.up(sizes.lg)} {
        padding: ${Spacing32} 0;
    }
`;

const Container = styled.div``;

const StyledUSPItem = styled(USPItem)`
    flex: 0 0 100%;
    padding-left: ${Spacing4};
    padding-right: ${Spacing4};

    ${breakpoint.up(sizes.md)} {
        flex: 0 0 50%;
        padding-left: ${Spacing32};
        padding-right: ${Spacing32};
        margin-left: 0;
        margin-right: 0;
    }
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
`;

const ArrowRightIcon = styled(Icon)`
    fill: ${ColorNeutralLightGrey};
`;

const ArrowLeftIcon = styled(ArrowRightIcon)`
    transform: rotate(180deg);
`;

const SlideIndexContainer = styled.div`
    padding-left: ${Spacing4};
    padding-right: ${Spacing4};
`;

const StyledScrollSnapSlider = styled(ScrollSnapSlider)`
    margin-left: -${Spacing4};
    margin-right: -${Spacing4};

    ${breakpoint.up(sizes.md)} {
        margin-left: 0;
        margin-right: 0;
    }

    ${invisibleScrollbar()}
`;

export type USPFooterType = {
    usps: FooterUspViewModel[];
};

function getOptions(usps: FooterUspViewModel[]): IOptions {
    const activeDurationMilliseconds =
        usps.find((ups) => ups.activeDurationMilliseconds)?.activeDurationMilliseconds || 8000;

    return { loop: true, autoplay: { intervalDuration: activeDurationMilliseconds, timeoutDuration: 3000 } };
}

export default function USPFooter(props: USPFooterType): JSX.Element {
    const { usps } = props;
    const optionsMemo = useMemo(() => getOptions(usps), [usps]);
    const { ref, slider } = useScrollSnapSlider(optionsMemo);
    const { t } = useTranslation();

    return (
        <Wrapper>
            <Container className="container-fluid">
                <ScrollSnapSliderContext.Provider value={slider}>
                    <StyledScrollSnapSlider ref={ref} pauseOnHover>
                        {usps.map((usp, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <StyledUSPItem key={i} usp={usp} data-slide-index={i} />
                        ))}
                    </StyledScrollSnapSlider>
                    <PaginationContainer>
                        <NavigationButton navType="prev" variant="ghostOnLight" size="sm" aria-label={t(ArrowLeft)}>
                            <ArrowLeftIcon type="ChevronRight" size={16} />
                        </NavigationButton>
                        <SlideIndexContainer>
                            <SlideIndex />/{usps.length}
                        </SlideIndexContainer>
                        <NavigationButton navType="next" variant="ghostOnLight" size="sm" aria-label={t(ArrowRight)}>
                            <ArrowRightIcon type="ChevronRight" size={16} />
                        </NavigationButton>
                    </PaginationContainer>
                </ScrollSnapSliderContext.Provider>
            </Container>
        </Wrapper>
    );
}
